import { useVirtualizer } from '@tanstack/react-virtual'
import FbButton from 'components/FbUI/FbButton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from 'components/UI/BaseDropdown/dropdown-base'
import { cn } from 'components/UI/cn'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Form } from 'react-bootstrap'
import { ImCheckboxChecked, ImCheckboxUnchecked } from 'react-icons/im'
import { FilterButton } from 'components/Filters/components/FilterButton'
import { FilterCloseButton } from 'components/Filters/components/FilterCloseButton'
import { useEventHistoryFilterStore } from '../EventHistoryFilterStore'
import apiService from 'services/api'

interface TeamMemberOption {
  value: number
  label: string
}

export function TeamMemberFilter() {
  const { useGetUsers } = apiService()
  const { data: users } = useGetUsers()

  const { pendingTeamMemberIds, setPendingTeamMemberIds } =
    useEventHistoryFilterStore()
  const isActive = !!pendingTeamMemberIds.length

  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [internalFilters, setInternalFilters] =
    useState<number[]>(pendingTeamMemberIds)

  // Convert users to options
  const options: TeamMemberOption[] = useMemo(() => {
    if (!users) return []
    return users.map((user) => ({
      value: user.id,
      label: `${user.first_name} ${user.last_name}`.trim() || user.email,
    }))
  }, [users])

  const filteredOptions = options
    .filter((o) => {
      const regex = new RegExp(search, 'gi')
      const labelTest = regex.test(o.label)
      const isSelected = internalFilters.includes(o.value)
      return isSelected || labelTest
    })
    .sort((a, b) => {
      const isASelected = internalFilters.includes(a.value) ? 1 : -1
      const isBSelected = internalFilters.includes(b.value) ? 1 : -1
      return isBSelected - isASelected
    })

  const activeValueLabel = useMemo(() => {
    const length = pendingTeamMemberIds?.length
    if (!length) return 'All'

    const firstActiveLabel = options.find(
      (o) => o.value === pendingTeamMemberIds[0]
    )
    if (length === 1 && firstActiveLabel) {
      return firstActiveLabel.label
    }

    return `${length} selected`
  }, [pendingTeamMemberIds, options])

  function handleInternalChange(option: TeamMemberOption, selected: boolean) {
    if (selected) {
      setInternalFilters((prev) => prev.filter((f) => f !== option.value))
    } else {
      setInternalFilters((prev) => [...prev, option.value])
    }
  }

  function selectAll() {
    setInternalFilters(options.map((o) => o.value))
  }

  function selectNone() {
    setInternalFilters([])
  }

  function clearFilters() {
    setPendingTeamMemberIds([])
  }

  function saveFilters() {
    setPendingTeamMemberIds(internalFilters)
  }

  // Syncs the internal filters with the filters in the store
  useEffect(() => {
    setInternalFilters(pendingTeamMemberIds)
  }, [pendingTeamMemberIds, open])

  function handleOpenChange(open: boolean) {
    if (!open) {
      // Apply filters when the dropdown closes
      saveFilters()
    }
    setOpen(open)
  }

  if (!options.length) return null

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <div className={'relative d-flex'}>
        <DropdownMenuTrigger asChild>
          <FilterButton
            open={open}
            title="Team Member"
            onClick={() => setOpen(!open)}
            isActive={isActive}
            activeValueLabel={activeValueLabel}
          />
        </DropdownMenuTrigger>
        <FilterCloseButton isActive={isActive} onClick={() => clearFilters()} />
      </div>

      <DropdownMenuContent
        forceMount={true}
        side={'bottom'}
        className={cn('bg-white w-[260px] lg:w-[235px] p-2')}
        sideOffset={5}
      >
        <Form.Control
          type="text"
          onChange={(e) => {
            setSearch(e.target.value)
          }}
          placeholder="Search"
        />

        <ItemsList
          options={filteredOptions}
          onSelect={handleInternalChange}
          selected={internalFilters}
        />
        <hr className={'my-2'} />
        <div className={'w-full'}>
          <div className={'flex items-center justify-between gap-2'}>
            <FbButton
              size={'sm'}
              className="flex-1"
              variant={'warning'}
              onClick={() => selectAll()}
            >
              All
            </FbButton>
            <FbButton
              size={'sm'}
              className="flex-1"
              variant={'secondary'}
              onClick={() => selectNone()}
            >
              None
            </FbButton>
          </div>

          <FbButton
            size={'sm'}
            className={'w-full mt-2'}
            onClick={() => {
              saveFilters()
              setOpen(false)
            }}
          >
            Apply
          </FbButton>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function ItemsList(props: {
  options: TeamMemberOption[]
  selected: number[]
  onSelect: (option: TeamMemberOption, selected: boolean) => void
}) {
  const parentRef = useRef<HTMLDivElement>(null)

  const virtual = useVirtualizer<HTMLDivElement, HTMLDivElement>({
    count: props.options?.length ?? 1,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 35,
    overscan: 5,
    measureElement:
      typeof window !== 'undefined' &&
      navigator.userAgent.indexOf('Firefox') === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
  })

  return (
    <div
      className={'mt-2 w-full'}
      ref={parentRef}
      style={{
        height: `150px`,
        width: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      <div
        style={{
          height: `${virtual.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
          contain: 'layout style paint',
          willChange: 'auto',
        }}
      >
        {virtual.getVirtualItems().map((item) => {
          const o = props.options[item.index]
          const isSelected = props.selected.includes(o?.value)
          if (!o) return null
          return (
            <div
              key={item.index}
              data-index={item.index}
              ref={(node) => virtual.measureElement(node)}
              className={cn(
                'max-w-full cursor-pointer flex items-center py-2 px-3 gap-2 hover:bg-gray-200 text-sm overflow-ellipsis whitespace-nowrap'
              )}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${item.size}px`,
                transform: `translate3d(0, ${item.start}px, 0)`,
                willChange: 'transform',
                backfaceVisibility: 'hidden',
              }}
              onClick={() => props.onSelect(o, isSelected)}
            >
              <div className={'flex-shrink-0'}>
                {isSelected ? <ImCheckboxChecked /> : <ImCheckboxUnchecked />}
              </div>
              <div
                className={
                  'overflow-ellipsis overflow-hidden whitespace-nowrap'
                }
              >
                {o.label}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
